import { useMemo } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useConfirmBeforeClose } from '@/hooks/useConfirmBeforeClose';
import { sortBySortOrder } from '@/utils/sort';
import { useSubmitSurveyAnswers } from '../hooks/useSubmitSurveyAnswers';
import type {
  SubmittedSurveyRef,
  SurveyAnswerFormValues,
  SurveyDetail,
} from '../types/survey.types';
import {
  buildAnswerFormSchema,
  buildDefaultAnswerValues,
  toSubmitSurveyPayload,
} from '../utils/surveyAnswerForm';
import { SurveyAnswerField } from './answer/SurveyAnswerField';

interface SurveyAnswerFormProps {
  survey: SurveyDetail;
  onCancel?: () => void;
  onSubmitted?: (submission: SubmittedSurveyRef) => void;
}

export function SurveyAnswerForm({ survey, onCancel, onSubmitted }: SurveyAnswerFormProps) {
  const sortedQuestions = useMemo(() => sortBySortOrder(survey.questions), [survey.questions]);

  const schema = useMemo(() => buildAnswerFormSchema(sortedQuestions), [sortedQuestions]);
  const defaultValues = useMemo(() => buildDefaultAnswerValues(sortedQuestions), [sortedQuestions]);

  const form = useForm<SurveyAnswerFormValues>({
    resolver: zodResolver(schema) as unknown as Resolver<SurveyAnswerFormValues>,
    defaultValues,
    mode: 'onBlur',
  });

  const { mutate: submitAnswers, isPending } = useSubmitSurveyAnswers(survey.id, (submission) => {
    form.reset(defaultValues);
    onSubmitted?.(submission);
  });

  const isDirty = form.formState.isDirty;
  const { askBeforeClose, discardChangesDialog } = useConfirmBeforeClose(isDirty && !isPending);

  function handleCancel() {
    if (onCancel) askBeforeClose(onCancel);
  }

  function handleSubmit(values: SurveyAnswerFormValues) {
    const payload = toSubmitSurveyPayload(sortedQuestions, values);
    if (payload.answers.length === 0) {
      toast.error('Please answer at least one question before submitting');
      return;
    }
    submitAnswers(payload);
  }

  function handleInvalid() {
    requestAnimationFrame(() => {
      const invalidEl = document.querySelector<HTMLElement>('[aria-invalid="true"]');
      if (invalidEl) {
        invalidEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    });
  }

  return (
    <FormProvider {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit, handleInvalid)}
        className="flex flex-1 flex-col gap-6"
        aria-busy={isPending || undefined}
      >
        <fieldset disabled={isPending} className="contents">
          <ol className="flex flex-col gap-3">
            {sortedQuestions.map((question, index) => (
              <li key={question.id}>
                <SurveyAnswerField question={question} index={index} />
              </li>
            ))}
          </ol>
        </fieldset>

        <div className="sticky bottom-0 flex flex-wrap items-center justify-end gap-2 border-t bg-background/95 px-1 py-3 pb-[max(--spacing(3),env(safe-area-inset-bottom))] backdrop-blur">
          {onCancel ? (
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isPending}>
              Cancel
            </Button>
          ) : null}
          <Button type="submit" disabled={isPending} className="gap-1.5">
            {isPending ? <Loader2 className="size-4 animate-spin" /> : null}
            {isPending ? 'Submitting...' : 'Submit Response'}
          </Button>
        </div>
      </form>
      {discardChangesDialog}
    </FormProvider>
  );
}
